import React, {useEffect, useMemo, useRef, useState} from "react";
import {readFileAsDataUrl, readFileAsText} from "../../../../core/services/utils/utils";
import useIsMounted from "../../../hooks/use-is-mounted";
import UploadButton from "../button/upload_button";
import {InputFileAcceptMimes, InputFileAcceptTypes} from "../../../../core/constants/enums";
import accepts from "attr-accept";
import {alpha, useTheme} from "@mui/material";
import classnames from "classnames";


const DropZone = ({
                      error = false,
                      disabled,
                      accept: acceptProps = [
                          InputFileAcceptMimes.images,
                          InputFileAcceptTypes.heic,
                          InputFileAcceptTypes.heif,
                      ],
                      returnFileName = false,
                      id,
                      onSuccess,
                      multiple = false,
                      children,
                      setTempUrl,
                      loading,
                      setLoading,
                      className,
                      identifier = '',
                      openOnClick = false,
                      uploading,
                      dropZoneClassName,
                      labelClassName,
                      noParse = false,
                      ...props
                  }) => {
    const [highlight, setHighlight] = useState(false);
    const [notAccepted, setNotAccepted] = useState(false);
    const inputRef = useRef();
    const isMounted = useIsMounted();
    const theme = useTheme();

    const accept = useMemo(() => {
        let accept = acceptProps ?? [];
        if (acceptProps.includes(InputFileAcceptMimes.videos)) {
            accept = [
                ...acceptProps.filter(e => e !== InputFileAcceptMimes.videos),
                ...InputFileAcceptMimes.videos.split(',')
            ]
        }
        accept = accept.reduce((p, c) => [...p, c, c.toUpperCase(), c.toLowerCase()], [])
        return [...(new Set(accept))]
    }, [acceptProps])

    /**
     * Listens for the changes in notAccepted and with each change:
     * if the component was in the not-accepted format, then clears the format after a second.
     */
    useEffect(() => {
        let timer;
        if (!notAccepted) {
            if (timer) return () => clearTimeout(timer);
            return;
        }
        timer = setTimeout(() => {
            if (!isMounted())
                return;
            setNotAccepted(false)
        }, 2000)
        return () => clearTimeout(timer)
    }, [notAccepted])

    /**
     * Opens the file dialog from the system.
     */
    const openFileDialog = () => {
        if (disabled || loading) return;
        inputRef?.current?.click()
    }

    /**
     * Highlights the dropzone if dragged over it.
     * @param e {Event}
     */
    const onDragOver = (e) => {
        e.preventDefault();
        if (disabled || loading) return;
        setHighlight(true);
    }

    /**
     * Un-Highlights the dropzone if leaves the draggable area it.
     */
    const onDragLeave = () => {
        setHighlight(false);
    }

    /**
     * Gets the dropped files and then un-highlights the drop-zone and calls onFilesSelected with the returned array
     * from fileListToArray function.
     * @param e {DragEvent}
     */
    const onDrop = async (e) => {
        e.preventDefault();
        if (disabled || loading || !e.dataTransfer?.files?.length) return;
        const files = await fileListToArray(e.dataTransfer.files);
        setHighlight(false);
        await onFilesSelected(files);
    }


    /**
     * For every selected file, if the file has the accepted types, returns the files array.
     * if no multiple, then only returns the first selected file.
     *
     * @param files {FileList}
     */
    const fileListToArray = async (files) => {
        const dataTransfer = new DataTransfer();
        for (let index = 0; index < files.length; index++) {
            let file = files.item(index);
            if (!accepts(file, accept))
                continue;
            if (multiple) {
                dataTransfer.items.add(file);
            } else {
                dataTransfer.items.add(file);
                break;
            }
        }
        if (dataTransfer.items.length === 0 && files.length > 0) {
            // none of the files are accepted
            setNotAccepted(true);
        }
        return dataTransfer.files;
    }

    /**
     * For each of the files, converts them to their base 64 string and then sends the converted array of
     * potentially filled as the argument of onSuccess callback.
     * @param files {FileList}
     */
    const onFilesSelected = async (files) => {
        if (disabled || loading || files?.length <= 0)
            return;

        const filesList = [];
        for (let i = 0; i < files.length; i++) {
            let file = files.item(i);
            if (file.name.endsWith(InputFileAcceptTypes.heic) || file.name.endsWith(InputFileAcceptTypes.heif) || file.type === 'image/heic' || file.type === 'image/heif') {
                const url = URL.createObjectURL(file);
                let blobRes = await fetch(url)
                let blob = await blobRes.blob()
                const heic2any = require('heic2any');
                let conversionResult = await heic2any({blob, quality: 1, type: 'image/png'});
                file = new File(Array.isArray(conversionResult) ? conversionResult : [conversionResult], file.name.replace(/\.heic|\.heif/g, '.png'), {type: 'image/png'})
            }
            filesList.push(file);
        }
        setLoading(true);
        if (noParse) {
            setLoading(false);
            onSuccess(filesList);
            return;
        }
        const res = [];
        for (let index = 0; index < filesList.length; index++) {
            const file = filesList[index];
            const images = [
                InputFileAcceptMimes.images,
                InputFileAcceptTypes.heic,
                InputFileAcceptTypes.heif,
            ]
            if (images.filter(e => !accepts(file, e)).length) {
                const text = await readFileAsText(file);
                if (!isMounted()) return;
                res.push(text);
            } else {
                const dataUrl = await readFileAsDataUrl(file);
                if (!isMounted()) return;
                if (setTempUrl) setTempUrl(dataUrl);
                res.push(dataUrl.split(',')[1]);
            }
            if (!multiple) break;
        }
        setLoading(false);
        onSuccess(res?.filter(e => e?.length), returnFileName ? filesList[0]?.name : null);
    }

    /**@type {CSSProperties}*/
    const style = useMemo(() => {
        if (error)
            return {backgroundImage: theme.palette['app-specific'].dashedBorder(theme.shape.borderRadius, alpha(theme.palette.error.main, 1))};
        if (notAccepted)
            return {backgroundImage: theme.palette['app-specific'].dashedBorder(theme.shape.borderRadius, alpha(theme.palette.warning.main, 1))};
        if (highlight)
            return {backgroundImage: theme.palette['app-specific'].dashedBorder(theme.shape.borderRadius, alpha(theme.palette.secondary.main, 1))};
        return {backgroundImage: theme.palette['app-specific'].dashedBorder(theme.shape.borderRadius, alpha(theme.palette.secondary.main, 0.2))};
    }, [error, theme.palette, theme.shape.borderRadius, notAccepted, highlight])


    return (
        <div
            className={classnames(`drop-zone`, {
                'high-light': highlight,
                'error': error,
                'not-accepted': notAccepted,
                'cursor-progress-hover': uploading,
                'cursor-not-allowed-hover': disabled || loading,
                'cursor-pointer-hover': !(disabled || loading),
                [identifier]: true,
                [dropZoneClassName]: true,
            })}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
            onClick={() => openOnClick && openFileDialog()}
            {...props}
            style={{
                ...props,
                ...style,
            }}
        >
            {
                openOnClick ? <UploadButton
                        labelClassName={labelClassName}
                        disabled={disabled || loading}
                        accept={accept}
                        ref={inputRef}
                        multiple={multiple}
                        id={`upload-button-${id}`}
                        onFileSelect={onFilesSelected}
                        className={className}
                    >
                        {typeof children === 'function' ? children(openFileDialog, {notAccepted, error}) : children}
                    </UploadButton>
                    : typeof children === 'function' ? children(openFileDialog, {notAccepted, error}) : children
            }

        </div>
    )
}


export default DropZone;
